import { Link } from 'react-router-dom';
import { Phone, Mail, MapPin, Clock, Instagram, ArrowRight, MessageCircle } from 'lucide-react';

const Contact = () => {
  return (
    <div className="min-h-screen pt-20" data-testid="contact-page">
      {/* Hero */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center px-4 py-2 bg-[#1E88E5]/10 rounded-full text-[#1E88E5] font-medium text-sm mb-6">
            Get In Touch
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-[#0B2C5D] mb-6">
            Contact Us
          </h1>
          <p className="text-lg text-[#64748B] max-w-3xl mx-auto">
            Have questions? We'd love to hear from you. Reach out to us through any of these channels.
          </p>
        </div>
      </section>

      {/* Contact Info */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Cards */}
            <div className="space-y-6">
              <h2 className="font-heading text-2xl font-bold text-[#0B2C5D] mb-8">
                Contact Information
              </h2>
              
              {/* Phone */}
              <a 
                href="tel:9044499957"
                className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group"
                data-testid="contact-phone"
              >
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center group-hover:bg-[#1E88E5] transition-colors">
                  <Phone className="h-6 w-6 text-[#1E88E5] group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-[#0B2C5D] mb-1">Phone</h3>
                  <p className="text-[#F7931E] font-semibold text-lg">9044499957</p>
                  <p className="text-sm text-[#64748B]">Call us for bookings & inquiries</p>
                </div>
              </a>

              {/* WhatsApp */}
              <a 
                href="https://wa.me/919044499957?text=Hi%20Washora,%20I'd%20like%20to%20know%20more%20about%20your%20services."
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group"
                data-testid="contact-whatsapp"
              >
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-500 transition-colors">
                  <MessageCircle className="h-6 w-6 text-green-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-[#0B2C5D] mb-1">WhatsApp</h3>
                  <p className="text-green-600 font-semibold text-lg">9044499957</p>
                  <p className="text-sm text-[#64748B]">Quick responses on WhatsApp</p>
                </div>
              </a>

              {/* Email */}
              <div className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-slate-100 shadow-sm">
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center">
                  <Mail className="h-6 w-6 text-[#1E88E5]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-[#0B2C5D] mb-1">Email</h3>
                  <a href="mailto:info@washora.org" className="text-[#1E88E5] hover:underline block">
                    info@washora.org
                  </a>
                  <a href="mailto:washorapremiumcare@gmail.com" className="text-[#1E88E5] hover:underline block">
                    washorapremiumcare@gmail.com
                  </a>
                </div>
              </div>

              {/* Address */}
              <div className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-slate-100 shadow-sm">
                <div className="w-14 h-14 bg-[#1E88E5]/10 rounded-xl flex items-center justify-center">
                  <MapPin className="h-6 w-6 text-[#1E88E5]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-[#0B2C5D] mb-1">Service Area</h3>
                  <p className="text-[#64748B]">
                    #3/21, 3rd Cross, Near Park<br />
                    Basaweshwara Nagar, Chitradurga - 577502<br />
                    Karnataka, India
                  </p>
                  <p className="text-sm text-[#F7931E] mt-2">Doorstep services across nearby areas</p>
                </div>
              </div>

              {/* Hours */}
              <div className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-slate-100 shadow-sm">
                <div className="w-14 h-14 bg-[#F7931E]/10 rounded-xl flex items-center justify-center">
                  <Clock className="h-6 w-6 text-[#F7931E]" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-[#0B2C5D] mb-1">Service Hours</h3>
                  <p className="text-[#64748B]">
                    7:00 AM - 7:00 PM<br />
                    <span className="text-[#F7931E] font-semibold">Open All Days</span>
                  </p>
                </div>
              </div>

              {/* Instagram */}
              <a 
                href="https://instagram.com/washora_premiumcare"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-4 p-6 bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-xl transition-all group"
                data-testid="contact-instagram"
              >
                <div className="w-14 h-14 bg-pink-100 rounded-xl flex items-center justify-center group-hover:bg-gradient-to-br group-hover:from-purple-500 group-hover:to-pink-500 transition-colors">
                  <Instagram className="h-6 w-6 text-pink-600 group-hover:text-white transition-colors" />
                </div>
                <div>
                  <h3 className="font-heading text-lg font-bold text-[#0B2C5D] mb-1">Instagram</h3>
                  <p className="text-pink-600 font-semibold">@washora_premiumcare</p>
                  <p className="text-sm text-[#64748B]">Follow us for updates</p>
                </div>
              </a>
            </div>

            {/* Map & CTA */}
            <div className="space-y-8">
              {/* Map */}
              <div className="rounded-2xl overflow-hidden h-80 shadow-lg">
                <iframe
                  src="https://www.google.com/maps?q=14.2401,76.402&z=15&output=embed"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Washora Location - Chitradurga"
                ></iframe>
              </div>

              {/* Quick CTA */}
              <div className="bg-gradient-to-br from-[#1E88E5] to-[#0B2C5D] rounded-2xl p-8 text-white">
                <h3 className="font-heading text-2xl font-bold mb-4">
                  Ready to Book?
                </h3>
                <p className="text-white/80 mb-6">
                  The easiest way to book is via WhatsApp. Quick responses guaranteed!
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/booking"
                    className="bg-[#F7931E] text-white px-6 py-3 rounded-full font-semibold hover:bg-[#E08215] transition-all flex items-center justify-center"
                    data-testid="contact-book-now"
                  >
                    Book Online <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                  <a
                    href="https://wa.me/919044499957?text=Hi%20Washora,%20I'd%20like%20to%20book%20a%20car%20wash%20service."
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-white text-[#0B2C5D] px-6 py-3 rounded-full font-semibold hover:bg-slate-100 transition-all flex items-center justify-center"
                  >
                    <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                    </svg>
                    WhatsApp
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
